import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput, TextArea } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";

export default function SiteSettings() {
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "settings");
  const [d, setD] = useState({ tagline: "", contact_email: "", contact_phone: "", address: "", risk_warning: "", footer_disclaimer: "", social: {}, hero_motion: { enabled: true, speed: 0.32 } });

  useEffect(() => { api.get("/settings/site").then((r) => setD({ social: {}, hero_motion: { enabled: true, speed: 0.32 }, ...r.data.data })).catch(() => {}); }, []);

  const save = async () => {
    try { await api.put("/settings/site", { data: d }); toast.success("Site settings saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const setSocial = (k) => (e) => setD({ ...d, social: { ...d.social, [k]: e.target.value } });

  return (
    <div>
      <AdminHeader title="Site Settings" subtitle="Brand, contact details, social links, and compliance copy (footer)." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-6 space-y-5">
          <h3 className="font-serif text-lg text-white">Brand & Contact</h3>
          <TextInput label="Tagline" value={d.tagline || ""} onChange={set("tagline")} disabled={!canEdit} testid="settings-tagline" />
          <TextInput label="Contact Email" value={d.contact_email || ""} onChange={set("contact_email")} disabled={!canEdit} testid="settings-email" />
          <TextInput label="Contact Phone" value={d.contact_phone || ""} onChange={set("contact_phone")} disabled={!canEdit} testid="settings-phone" />
          <TextInput label="Office Address" value={d.address || ""} onChange={set("address")} disabled={!canEdit} testid="settings-address" />
        </Panel>

        <Panel className="p-6 space-y-5">
          <h3 className="font-serif text-lg text-white">Social Links</h3>
          {["twitter", "linkedin", "facebook", "instagram", "youtube"].map((s) => (
            <TextInput key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} value={(d.social || {})[s] || ""} onChange={setSocial(s)} disabled={!canEdit} testid={`settings-social-${s}`} />
          ))}
        </Panel>

        <Panel className="p-6 space-y-5">
          <h3 className="font-serif text-lg text-white">Hero Animation</h3>
          <p className="text-xs text-slate-500">Controls the looping market-motion candlesticks behind the homepage hero.</p>
          <label className="flex items-center justify-between text-sm text-slate-300">
            <span>Show market-motion loop</span>
            <input type="checkbox" checked={(d.hero_motion?.enabled ?? true)} onChange={(e) => setD({ ...d, hero_motion: { ...(d.hero_motion || {}), enabled: e.target.checked } })} disabled={!canEdit} data-testid="settings-motion-enabled" />
          </label>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Animation speed</label>
            <select value={String(d.hero_motion?.speed ?? 0.32)} onChange={(e) => setD({ ...d, hero_motion: { ...(d.hero_motion || {}), speed: parseFloat(e.target.value) } })} disabled={!canEdit} data-testid="settings-motion-speed" className="w-full bg-[#0B1D40] border border-[#203E70] rounded px-3 py-2 text-sm text-white">
              <option value="0.15">Slow</option>
              <option value="0.32">Normal</option>
              <option value="0.6">Fast</option>
            </select>
          </div>
        </Panel>

        <Panel className="p-6 space-y-5 lg:col-span-2">
          <h3 className="font-serif text-lg text-white">Compliance & Risk Copy</h3>
          <p className="text-xs text-slate-500">Shown in the footer. Leave empty until you have verified, approved text — no placeholder claims.</p>
          <TextArea label="Risk Warning" rows={3} value={d.risk_warning || ""} onChange={set("risk_warning")} disabled={!canEdit} testid="settings-risk" />
          <TextArea label="Footer Disclaimer / Regulatory Notice" rows={3} value={d.footer_disclaimer || ""} onChange={set("footer_disclaimer")} disabled={!canEdit} testid="settings-disclaimer" />
          <TextArea label="Regulators & Audit (one per line — shown as a footer band; leave empty to hide)" rows={3} value={d.regulators || ""} onChange={set("regulators")} disabled={!canEdit} testid="settings-regulators" />
        </Panel>
      </div>
      {canEdit && <button type="button" onClick={save} className="btn-gold px-6 py-2.5 text-sm mt-6" data-testid="settings-save">Save Settings</button>}
    </div>
  );
}
